import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusBadgeStatus } from '@xxtract/ui';
import { useBatchHistory } from '../hooks/useDataQuality';
import { CalculatedAtCaption } from './CalculatedAtCaption';
import type { BatchRunStatus } from '../data-quality.api';

const BATCH_STATUS_TO_BADGE: Record<BatchRunStatus, StatusBadgeStatus> = {
  running: 'service-in-progress',
  completed: 'service-completed',
  failed: 'service-degraded',
};

/** FR25: batch-status/-historie ("laatste run: vannacht 02:14, 1.240 producten"). */
export function BatchStatusPanel({ gln }: { gln: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useBatchHistory(gln);
  const batches = data?.data.batches ?? [];
  const latest = batches[0];

  if (isLoading) {
    return <div data-testid="batch-status-panel-skeleton" className="skeleton h-16 rounded-xl" />;
  }

  if (!latest) {
    return null;
  }

  return (
    <div
      data-testid="batch-status-panel"
      className="rounded-xl p-4"
      style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold" style={{ color: 'var(--foreground-heading)' }}>
            {t('dataQuality.batch.title')}
          </p>
          <p className="mt-0.5 text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('dataQuality.batch.summary', { count: latest.productCount, errorCount: latest.errorCount })}
          </p>
        </div>
        <StatusBadge status={BATCH_STATUS_TO_BADGE[latest.status]} label={t(`dataQuality.batch.status.${latest.status}`)} />
      </div>
      <CalculatedAtCaption isoDate={latest.startedAt} className="mt-2" />
    </div>
  );
}
